import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import type { ThemeMode } from '@/context/ThemeContext';

const THEME_OPTIONS: { mode: ThemeMode; icon: string; label: string }[] = [
  { mode: 'light', icon: 'ri-sun-line', label: 'Claro' },
  { mode: 'dark', icon: 'ri-moon-line', label: 'Oscuro' },
  { mode: 'system', icon: 'ri-computer-line', label: 'Sistema' },
];

interface AccountMenuProps {
  onSignOut: () => void;
}

/**
 * Avatar de cuenta con menú desplegable: datos del usuario, apariencia
 * (claro / oscuro / sistema) y cerrar sesión. Funciona en modo claro y oscuro.
 */
export default function AccountMenu({ onSignOut }: AccountMenuProps) {
  const { profile, isAdmin } = useAuth();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const fullName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    profile?.email ||
    'Usuario';
  const initials = fullName.charAt(0).toUpperCase();
  const roleLabel = isAdmin ? 'Administrador' : 'Cajero';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center font-semibold shrink-0 border border-background-200/70 cursor-pointer transition hover:bg-secondary-200"
        title={fullName}
      >
        {initials}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-64 rounded-xl border border-background-200/70 bg-background-50/95 backdrop-blur-md p-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center font-semibold shrink-0">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground-950 truncate">{fullName}</p>
              <p className="text-xs text-foreground-500">{roleLabel}</p>
            </div>
          </div>

          <div className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-400 mb-1.5">
              Apariencia
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {THEME_OPTIONS.map((o) => (
                <button
                  key={o.mode}
                  onClick={() => setTheme(o.mode)}
                  className={`flex flex-col items-center gap-1 rounded-md border px-1 py-2 text-[11px] font-medium transition cursor-pointer ${
                    theme === o.mode
                      ? 'border-primary-300 bg-primary-50 text-primary-700'
                      : 'border-background-200 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${o.icon} text-base`} />
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="mt-3 w-full flex items-center justify-center gap-2 rounded-md border border-background-200 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 transition whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-r-line" /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}